import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Card, Row, Col, Form, Button, Alert, Badge, Modal } from 'react-bootstrap';
import { Cog8ToothIcon, GlobeAltIcon, PhotoIcon, ArrowTopRightOnSquareIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import CropModal from '../common/CropModal';
import { companyAPI, paymentsAPI } from '../../services/api';

const COLOR_THEMES = [
  { value: 'sage',  label: 'Sage Green',   bg: '#d1fae5', accent: '#059669' },
  { value: 'wheat', label: 'Warm Wheat',   bg: '#fef3c7', accent: '#d97706' },
  { value: 'stone', label: 'Stone Grey',   bg: '#f1f5f9', accent: '#64748b' },
  { value: 'earth', label: 'Earth Brown',  bg: '#fdf4e7', accent: '#92400e' },
  { value: 'cream', label: 'Cream & Navy', bg: '#eff6ff', accent: '#1e40af' },
  { value: 'custom', label: '🎨 Custom',   bg: '#f8f8f8', accent: '#334155' },
];

// Full preset colours used to pre-fill custom pickers
const PRESET_COLORS: Record<string, { primary: string; primaryDark: string; bg: string; cardBorder: string }> = {
  sage:  { primary: '#059669', primaryDark: '#047857', bg: '#f0fdf4', cardBorder: '#bbf7d0' },
  wheat: { primary: '#d97706', primaryDark: '#b45309', bg: '#fffbeb', cardBorder: '#fde68a' },
  stone: { primary: '#475569', primaryDark: '#334155', bg: '#f8fafc', cardBorder: '#cbd5e1' },
  earth: { primary: '#92400e', primaryDark: '#78350f', bg: '#fdf4e7', cardBorder: '#fbbf24' },
  cream: { primary: '#1e40af', primaryDark: '#1e3a8a', bg: '#eff6ff', cardBorder: '#bfdbfe' },
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const sectionLabel: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#9ca3af',
  marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9',
};

// 3×3 position picker ─────────────────────────────────────────
const POSITION_GRID = [
  ['top-left',    'top-center',    'top-right'],
  ['middle-left', 'middle-center', 'middle-right'],
  ['bottom-left', 'bottom-center', 'bottom-right'],
];

function PositionPicker({ value, onChange, accent = '#059669' }: { value: string; onChange: (v: string) => void; accent?: string }) {
  return (
    <div style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 30px)', gap: 4, padding: 5, background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
      {POSITION_GRID.flat().map(pos => (
        <button key={pos} title={pos.replace('-', ' ')} onClick={() => onChange(pos)}
          style={{ width: 30, height: 30, borderRadius: '6px', border: `2px solid ${value === pos ? accent : 'transparent'}`, background: value === pos ? accent + '18' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: value === pos ? accent : '#cbd5e1' }} />
        </button>
      ))}
    </div>
  );
}

export default function WebstoreSettings() {
  const [form, setForm] = useState<any>({
    store_name: '', tagline: '', color_theme: 'sage',
    meta_title: '', meta_description: '', ga_tracking_id: '',
    bake_days: [], last_order_days: [], last_order_time: '',
    order_lead_days: 1, stripe_price_id: '', is_active: false,
    bake_schedule_type: 'days_of_week', bake_exact_dates: [],
    login_style: 'modal',
    tax_rate: 0, tax_label: 'Tax',
    platform_fee_pct: 0,  // read-only, from subscription plan
    platform_fee_customer_pct: 0,
    cc_fee_customer_pct: 0,
    fulfillment_mode: 'both', delivery_fee: 0,
    tip_enabled: false, tip_percentages: [10, 15, 20],
    logo_shape: 'circle', logo_border: 'medium',
    logo_position: 'middle-center', logo_size: 'medium',
    name_position: 'middle-center',
    hide_hero: false,
    show_schedule_in_hero: false, schedule_position: 'bottom-center', schedule_size: 'medium',
    navbar_show_brand: true, navbar_menu_align: 'left',
    show_home_bakery_statement: false,
    home_bakery_statement: 'This product was produced in a private residence that is not subject to governmental licensing or inspection.',
    footer_about: '', contact_email: '',
    instagram_url: '', facebook_url: '', tiktok_url: '',
    show_contact_form: true,
    custom_theme: { primary: '#059669', primaryDark: '#047857', bg: '#f0fdf4', cardBorder: '#bbf7d0' },
    custom_domain: '',
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [heroPreview, setHeroPreview] = useState<string | null>(null);
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);

  // Crop modal state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropTarget, setCropTarget] = useState<'logo' | 'hero' | null>(null);
  const [cropFileName, setCropFileName] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);

  const openCrop = (file: File, target: 'logo' | 'hero') => {
    setCropSrc(URL.createObjectURL(file));
    setCropTarget(target);
    setCropFileName(file.name);
  };

  const handleCropDone = (file: File, previewUrl: string) => {
    if (cropTarget === 'logo') { setLogoFile(file); setLogoPreview(previewUrl); }
    if (cropTarget === 'hero') { setHeroFile(file); setHeroPreview(previewUrl); }
    setCropSrc(null); setCropTarget(null);
  };
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [subdomain, setSubdomain] = useState('');
  const [customDomainEnabled, setCustomDomainEnabled] = useState(false);

  // Stripe Connect
  const [connectStatus, setConnectStatus] = useState<any>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [platformFeePct, setPlatformFeePct] = useState<string | null>(null);

  // Retail products panel
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Pages panel
  const [pages, setPages] = useState<any[]>([]);
  const [pageModal, setPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState<any | null>(null);
  const [pageForm, setPageForm] = useState({ title: '', slug: '', content: '', is_published: true, sort_order: 0 });
  const [pageFormError, setPageFormError] = useState('');

  // Contact messages
  const [contactMessages, setContactMessages] = useState<any[]>([]);
  const [contactMsg, setContactMsg] = useState<any | null>(null);

  const load = useCallback(async () => {
    try {
      const [settingsRes, productsRes, pagesRes, contactRes] = await Promise.all([
        companyAPI.getStoreSettings(),
        companyAPI.getStoreProducts(),
        companyAPI.getStorePages(),
        companyAPI.getContactMessages(),
      ]);
      const d = settingsRes.data;
      setForm({
        store_name: d.store_name || '',
        tagline: d.tagline || '',
        color_theme: d.color_theme || 'sage',
        meta_title: d.meta_title || '',
        meta_description: d.meta_description || '',
        ga_tracking_id: d.ga_tracking_id || '',
        bake_days: d.bake_days || [],
        last_order_days: d.last_order_days || [],
        last_order_time: d.last_order_time || '',
        order_lead_days: d.order_lead_days ?? 1,
        bake_schedule_type: d.bake_schedule_type || 'days_of_week',
        bake_exact_dates: d.bake_exact_dates || [],
        login_style: d.login_style || 'modal',
        tax_rate: d.tax_rate ?? 0,
        tax_label: d.tax_label || 'Tax',
        platform_fee_pct: d.platform_fee_pct ?? 0,
        platform_fee_customer_pct: d.platform_fee_customer_pct ?? 0,
        cc_fee_customer_pct: d.cc_fee_customer_pct ?? 0,
        fulfillment_mode: d.fulfillment_mode || 'both',
        delivery_fee: d.delivery_fee ?? 0,
        tip_enabled: d.tip_enabled || false,
        tip_percentages: d.tip_percentages?.length ? d.tip_percentages : [10, 15, 20],
        stripe_price_id: d.stripe_price_id || '',
        is_active: d.is_active || false,
        logo_shape:    d.logo_shape    || 'circle',
        logo_border:   d.logo_border   || 'medium',
        logo_position: d.logo_position || 'middle-center',
        logo_size:     d.logo_size     || 'medium',
        name_position: d.name_position || 'middle-center',
        hide_hero: d.hide_hero || false,
        show_schedule_in_hero: d.show_schedule_in_hero || false,
        schedule_position: d.schedule_position || 'bottom-center',
        schedule_size: d.schedule_size || 'medium',
        navbar_show_brand: d.navbar_show_brand !== false,
        navbar_menu_align: d.navbar_menu_align || 'left',
        show_home_bakery_statement: d.show_home_bakery_statement || false,
        home_bakery_statement: d.home_bakery_statement || 'This product was produced in a private residence that is not subject to governmental licensing or inspection.',
        footer_about: d.footer_about || '',
        contact_email: d.contact_email || '',
        instagram_url: d.instagram_url || '',
        facebook_url: d.facebook_url || '',
        tiktok_url: d.tiktok_url || '',
        show_contact_form: d.show_contact_form !== false,
        custom_theme: d.custom_theme && Object.keys(d.custom_theme).length > 0
          ? d.custom_theme
          : { primary: '#059669', primaryDark: '#047857', bg: '#f0fdf4', cardBorder: '#bbf7d0' },
        custom_domain: d.custom_domain || '',
      });
      if (d.logo) setLogoPreview(d.logo);
      if (d.hero_image) setHeroPreview(d.hero_image);
      setAllProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setPages(Array.isArray(pagesRes.data) ? pagesRes.data : []);
      setContactMessages(Array.isArray(contactRes.data) ? contactRes.data : []);
    } catch {
      setFeedback({ error: 'Failed to load store settings.', success: '' });
    } finally {
      setLoading(false);
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setSubdomain(user.subdomain || user.username || '');
    setCustomDomainEnabled(!!user.custom_domain_enabled);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    paymentsAPI.getConnectStatus().then(res => {
      setConnectStatus(res.data);
      if (res.data?.platform_fee_pct) setPlatformFeePct(res.data.platform_fee_pct);
    }).catch(() => {});
  }, []);

  // Handle Stripe Connect return (after onboarding)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'success') {
      paymentsAPI.getConnectStatus().then(res => setConnectStatus(res.data)).catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const toggleProductRetail = async (product: any) => {
    setTogglingId(product.id);
    try {
      await companyAPI.setRetailPublished([product.id], !product.retail_published);
      setAllProducts(prev => prev.map(p => p.id === product.id ? { ...p, retail_published: !p.retail_published } : p));
    } catch {}
    setTogglingId(null);
  };

  const toggleDay = (field: 'bake_days' | 'last_order_days', day: number) => {
    setForm((p: any) => ({
      ...p,
      [field]: p[field].includes(day) ? p[field].filter((d: number) => d !== day) : [...p[field], day],
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    if (logoInputRef.current) logoInputRef.current.value = '';
    openCrop(file, 'logo');
  };

  const handleHeroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (heroInputRef.current) heroInputRef.current.value = '';
    openCrop(file, 'hero');
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback({ error: '', success: '' });
    try {
      if (logoFile) {
        await companyAPI.uploadStoreLogo(logoFile);
        setLogoFile(null);
      }
      if (heroFile) {
        await companyAPI.uploadStoreHero(heroFile);
        setHeroFile(null);
      }
      await companyAPI.updateStoreSettings(form);
      setFeedback({ error: '', success: 'Store settings saved.' });
    } catch (e: any) {
      setFeedback({ error: e?.response?.data?.error || 'Failed to save.', success: '' });
    } finally {
      setSaving(false);
    }
  };

  const storeUrl = subdomain ? `https://${subdomain}.sourdoughsy.com/store` : null;

  if (loading) return <CompanyLayout><div className="text-center py-5"><div className="spinner-border text-success" /></div></CompanyLayout>;

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-4">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
              <GlobeAltIcon style={{ width: 22, height: 22, marginRight: 8, color: '#059669', verticalAlign: 'middle' }} />
              Webstore Settings
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              Configure your public retail store.
              {storeUrl && <a href={storeUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#059669', fontSize: '0.82rem' }}>
                View store <ArrowTopRightOnSquareIcon style={{ width: 12, height: 12 }} />
              </a>}
            </p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Badge bg="" style={{ background: form.is_active ? '#dcfce7' : '#fee2e2', color: form.is_active ? '#166534' : '#991b1b', padding: '6px 12px', borderRadius: '20px', fontSize: '0.8rem' }}>
              {form.is_active ? 'Store Live' : 'Store Offline'}
            </Badge>
            <Form.Check type="switch" checked={form.is_active} onChange={e => setForm((p: any) => ({ ...p, is_active: e.target.checked }))} label="" />
            <Button variant="success" style={{ borderRadius: '10px', fontWeight: 700, fontSize: '0.88rem', background: '#059669', border: 'none', padding: '8px 18px', whiteSpace: 'nowrap' }} onClick={handleSave} disabled={saving}>
              <Cog8ToothIcon style={{ width: 16, height: 16, marginRight: 6 }} />
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </div>

        {feedback.error && <Alert variant="danger" style={{ borderRadius: '12px' }}>{feedback.error}</Alert>}
        {feedback.success && <Alert variant="success" style={{ borderRadius: '12px' }}>{feedback.success}</Alert>}

        <Row className="g-4">
          <Col lg={6}>
            {/* Branding */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>BRANDING</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Store Name</Form.Label>
                    <Form.Control size="sm" value={form.store_name} onChange={e => setForm((p: any) => ({ ...p, store_name: e.target.value }))} placeholder="Dough'n Roll Bakery" />
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Tagline</Form.Label>
                    <Form.Control size="sm" value={form.tagline} onChange={e => setForm((p: any) => ({ ...p, tagline: e.target.value }))} placeholder="Fresh baked with love, every morning." />
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Logo</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      {logoPreview
                        ? <img src={logoPreview} alt="logo" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0', cursor: 'pointer' }} onClick={() => logoInputRef.current?.click()} />
                        : <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px dashed #cbd5e1' }} onClick={() => logoInputRef.current?.click()}>
                            <PhotoIcon style={{ width: 24, height: 24, color: '#cbd5e1' }} />
                          </div>
                      }
                      <div>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={() => logoInputRef.current?.click()}>
                            {logoPreview ? 'Change & Crop' : 'Upload & Crop'}
                          </Button>
                          {logoPreview && (
                            <Button size="sm" variant="outline-danger" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={async () => {
                              try { await companyAPI.deleteStoreLogo(); setLogoPreview(null); setLogoFile(null); } catch {}
                            }}>
                              Delete
                            </Button>
                          )}
                        </div>
                        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px' }}>Square (1:1) · Shown in hero</div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Hero Image</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      {heroPreview
                        ? <img src={heroPreview} alt="hero" style={{ width: 96, height: 54, borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => heroInputRef.current?.click()} />
                        : <div style={{ width: 96, height: 54, borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px dashed #cbd5e1' }} onClick={() => heroInputRef.current?.click()}>
                            <PhotoIcon style={{ width: 22, height: 22, color: '#cbd5e1' }} />
                          </div>
                      }
                      <div>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={() => heroInputRef.current?.click()}>
                            {heroPreview ? 'Change & Crop' : 'Upload & Crop'}
                          </Button>
                          {heroPreview && (
                            <Button size="sm" variant="outline-danger" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={async () => {
                              try { await companyAPI.deleteStoreHero(); setHeroPreview(null); setHeroFile(null); } catch {}
                            }}>
                              Delete
                            </Button>
                          )}
                        </div>
                        <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroChange} style={{ display: 'none' }} />
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px' }}>Widescreen (16:9) · Store banner</div>
                        <Form.Check
                          type="switch"
                          id="hide-hero-switch"
                          label={<span style={{ fontSize: '0.78rem', color: '#374151' }}>Hide hero banner</span>}
                          checked={form.hide_hero || false}
                          onChange={e => setForm((p: any) => ({ ...p, hide_hero: e.target.checked }))}
                          style={{ marginTop: 6 }}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Color Theme</Form.Label>
                    <div className="d-flex gap-2 flex-wrap mt-1">
                      {COLOR_THEMES.map(t => (
                        <button key={t.value} onClick={() => {
                          if (t.value !== 'custom' && PRESET_COLORS[t.value]) {
                            // selecting a preset — also pre-fill the custom pickers so tweaking is easy
                            setForm((p: any) => ({ ...p, color_theme: t.value, custom_theme: PRESET_COLORS[t.value] }));
                          } else {
                            // switching to custom — keep current custom_theme colours
                            setForm((p: any) => ({ ...p, color_theme: t.value }));
                          }
                        }}
                          style={{ padding: '6px 14px', borderRadius: '20px', border: `2px solid ${form.color_theme === t.value ? t.accent : '#e2e8f0'}`, background: t.bg, color: t.accent, fontWeight: form.color_theme === t.value ? 700 : 400, fontSize: '0.8rem', cursor: 'pointer' }}>
                          {t.label}
                        </button>
                      ))}
                    </div>

                    {/* Custom colour pickers — shown when custom theme is active */}
                    {form.color_theme === 'custom' && (
                      <div style={{ marginTop: 14, background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Custom Colors</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                          {([
                            { key: 'primary',     label: 'Brand Color',   hint: 'Buttons, links, badges' },
                            { key: 'primaryDark', label: 'Dark Accent',   hint: 'Footer, hover states' },
                            { key: 'bg',          label: 'Page Background', hint: 'Main page bg' },
                            { key: 'cardBorder',  label: 'Card Border',   hint: 'Product card border' },
                          ] as { key: string; label: string; hint: string }[]).map(({ key, label, hint }) => (
                            <div key={key}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                  type="color"
                                  value={form.custom_theme?.[key] || '#059669'}
                                  onChange={e => setForm((p: any) => ({ ...p, custom_theme: { ...p.custom_theme, [key]: e.target.value } }))}
                                  style={{ width: 40, height: 32, border: 'none', borderRadius: '6px', cursor: 'pointer', padding: 0 }}
                                />
                                <input
                                  type="text"
                                  value={form.custom_theme?.[key] || '#059669'}
                                  onChange={e => {
                                    const val = e.target.value;
                                    if (/^#[0-9a-fA-F]{0,6}$/.test(val)) setForm((p: any) => ({ ...p, custom_theme: { ...p.custom_theme, [key]: val } }));
                                  }}
                                  style={{ width: 90, fontSize: '0.8rem', fontFamily: 'monospace', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 8px' }}
                                />
                              </div>
                              <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 3 }}>{hint}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: 12, fontSize: '0.75rem', color: '#94a3b8' }}>
                          💡 Tip: select a preset first, then switch to Custom to tweak from there.
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>

                {/* ── Hero Layout ─────────────────────────────── */}
                <div style={{ ...sectionLabel, marginTop: 20 }}>HERO LAYOUT</div>
                <Row className="g-3">
                  {/* Logo shape */}
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Logo Shape</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {(['none','circle','rounded','square'] as const).map(s => (
                        <button key={s} onClick={() => setForm((p: any) => ({ ...p, logo_shape: s }))}
                          style={{ padding: '5px 14px', borderRadius: '20px', border: `2px solid ${form.logo_shape === s ? '#059669' : '#e2e8f0'}`, background: form.logo_shape === s ? '#f0fdf4' : 'white', color: form.logo_shape === s ? '#059669' : '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Col>

                  {/* Logo border */}
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Logo Border</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {(['none','thin','medium','thick'] as const).map(b => (
                        <button key={b} onClick={() => setForm((p: any) => ({ ...p, logo_border: b }))}
                          style={{ padding: '5px 14px', borderRadius: '20px', border: `2px solid ${form.logo_border === b ? '#059669' : '#e2e8f0'}`, background: form.logo_border === b ? '#f0fdf4' : 'white', color: form.logo_border === b ? '#059669' : '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                          {b.charAt(0).toUpperCase() + b.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Col>

                  {/* Logo size */}
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Logo Size</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {(['small','medium','large','xl','xxl'] as const).map(sz => (
                        <button key={sz} onClick={() => setForm((p: any) => ({ ...p, logo_size: sz }))}
                          style={{ padding: '5px 14px', borderRadius: '20px', border: `2px solid ${form.logo_size === sz ? '#059669' : '#e2e8f0'}`, background: form.logo_size === sz ? '#f0fdf4' : 'white', color: form.logo_size === sz ? '#059669' : '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                          {sz.charAt(0).toUpperCase() + sz.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Col>

                  {/* Position pickers side by side */}
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Logo Position</Form.Label>
                    <div className="mt-1">
                      <PositionPicker value={form.logo_position} onChange={v => setForm((p: any) => ({ ...p, logo_position: v }))} accent="#059669" />
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 5 }}>{form.logo_position.replace('-', ' ')}</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Name &amp; Tagline Position</Form.Label>
                    <div className="mt-1">
                      <PositionPicker value={form.name_position} onChange={v => setForm((p: any) => ({ ...p, name_position: v }))} accent="#6366f1" />
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 5 }}>{form.name_position.replace('-', ' ')}</div>
                    </div>
                  </Col>

                  {/* Schedule in hero */}
                  <Col xs={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <Form.Check
                        type="switch"
                        id="show-schedule-hero"
                        checked={form.show_schedule_in_hero}
                        onChange={e => setForm((p: any) => ({ ...p, show_schedule_in_hero: e.target.checked }))}
                      />
                      <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                        Show Bake &amp; Order Schedule in Hero
                      </Form.Label>
                    </div>
                    {form.show_schedule_in_hero && (
                      <Row className="g-2 mt-1">
                        <Col xs={6}>
                          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Schedule Position</Form.Label>
                          <div className="mt-1">
                            <PositionPicker value={form.schedule_position} onChange={v => setForm((p: any) => ({ ...p, schedule_position: v }))} accent="#f59e0b" />
                            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 5 }}>{form.schedule_position.replace('-', ' ')}</div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Schedule Size</Form.Label>
                          <div className="d-flex flex-column gap-1 mt-1">
                            {(['small', 'medium', 'large'] as const).map(sz => (
                              <button key={sz} onClick={() => setForm((p: any) => ({ ...p, schedule_size: sz }))}
                                style={{ padding: '4px 14px', borderRadius: '20px', border: `2px solid ${form.schedule_size === sz ? '#f59e0b' : '#e2e8f0'}`, background: form.schedule_size === sz ? '#fffbeb' : 'white', color: form.schedule_size === sz ? '#b45309' : '#475569', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', textAlign: 'left' }}>
                                {sz.charAt(0).toUpperCase() + sz.slice(1)}
                              </button>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>

                {/* ── Navbar ────────────────────────────────────── */}
                <div style={{ ...sectionLabel, marginTop: 20 }}>NAVBAR</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Form.Check
                        type="switch"
                        id="navbar-show-brand"
                        checked={form.navbar_show_brand}
                        onChange={e => setForm((p: any) => ({ ...p, navbar_show_brand: e.target.checked }))}
                      />
                      <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                        Show store name in navbar
                      </Form.Label>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Menu Alignment</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {(['left', 'center', 'right'] as const).map(align => (
                        <button key={align} onClick={() => setForm((p: any) => ({ ...p, navbar_menu_align: align }))}
                          style={{ padding: '5px 16px', borderRadius: '20px', border: `2px solid ${form.navbar_menu_align === align ? '#059669' : '#e2e8f0'}`, background: form.navbar_menu_align === align ? '#f0fdf4' : 'white', color: form.navbar_menu_align === align ? '#059669' : '#475569', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                          {align.charAt(0).toUpperCase() + align.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* SEO & Analytics */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>SEO & ANALYTICS</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Page Title</Form.Label>
                    <Form.Control size="sm" value={form.meta_title} onChange={e => setForm((p: any) => ({ ...p, meta_title: e.target.value }))} placeholder="Fresh Sourdough Bread — Order Online" />
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Meta Description</Form.Label>
                    <Form.Control as="textarea" rows={2} size="sm" value={form.meta_description} onChange={e => setForm((p: any) => ({ ...p, meta_description: e.target.value }))} placeholder="Order fresh sourdough bread baked to order…" />
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Google Analytics ID</Form.Label>
                    <Form.Control size="sm" value={form.ga_tracking_id} onChange={e => setForm((p: any) => ({ ...p, ga_tracking_id: e.target.value }))} placeholder="G-XXXXXXXXXX" />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            {/* Home Bakery Statement */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>LEGAL STATEMENT</div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Form.Check
                    type="switch"
                    id="show-homebakery"
                    checked={form.show_home_bakery_statement}
                    onChange={e => setForm((p: any) => ({ ...p, show_home_bakery_statement: e.target.checked }))}
                  />
                  <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                    Show home bakery statement on store
                  </Form.Label>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.home_bakery_statement}
                  onChange={e => setForm((p: any) => ({ ...p, home_bakery_statement: e.target.value }))}
                  style={{ fontSize: '0.82rem', fontStyle: form.show_home_bakery_statement ? 'normal' : 'normal', borderColor: '#e2e8f0' }}
                />
                <Form.Text muted style={{ fontSize: '0.72rem' }}>
                  Displayed as a banner at the bottom of your public store when enabled.
                </Form.Text>
              </Card.Body>
            </Card>

            {/* Footer & Contact */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>FOOTER & CONTACT</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>About / Footer Text</Form.Label>
                    <Form.Control as="textarea" rows={2} size="sm" value={form.footer_about} onChange={e => setForm((p: any) => ({ ...p, footer_about: e.target.value }))} placeholder="A short description shown in the footer…" />
                  </Col>
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Contact Email</Form.Label>
                    <Form.Control type="email" size="sm" value={form.contact_email} onChange={e => setForm((p: any) => ({ ...p, contact_email: e.target.value }))} placeholder="hello@yourbakery.com" />
                  </Col>
                  <Col xs={4}>
                    <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Instagram URL</Form.Label>
                    <Form.Control size="sm" value={form.instagram_url} onChange={e => setForm((p: any) => ({ ...p, instagram_url: e.target.value }))} placeholder="https://instagram.com/..." />
                  </Col>
                  <Col xs={4}>
                    <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Facebook URL</Form.Label>
                    <Form.Control size="sm" value={form.facebook_url} onChange={e => setForm((p: any) => ({ ...p, facebook_url: e.target.value }))} placeholder="https://facebook.com/..." />
                  </Col>
                  <Col xs={4}>
                    <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>TikTok URL</Form.Label>
                    <Form.Control size="sm" value={form.tiktok_url} onChange={e => setForm((p: any) => ({ ...p, tiktok_url: e.target.value }))} placeholder="https://tiktok.com/@..." />
                  </Col>
                  <Col xs={12}>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check type="switch" id="show-contact" checked={form.show_contact_form} onChange={e => setForm((p: any) => ({ ...p, show_contact_form: e.target.checked }))} />
                      <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>Show contact form on store</Form.Label>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Ordering Rules */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>ORDERING RULES</div>
                <Row className="g-3">
                  {/* Bake Schedule Mode */}
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Bake Schedule Mode</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {[{ val: 'days_of_week', label: 'Days of Week' }, { val: 'exact_dates', label: 'Exact Dates' }].map(opt => (
                        <button key={opt.val} onClick={() => setForm((p: any) => ({ ...p, bake_schedule_type: opt.val }))}
                          style={{ padding: '8px 20px', borderRadius: '20px', border: `2px solid ${form.bake_schedule_type === opt.val ? '#059669' : '#e2e8f0'}`, background: form.bake_schedule_type === opt.val ? '#059669' : 'white', color: form.bake_schedule_type === opt.val ? 'white' : '#374151', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </Col>
                  {form.bake_schedule_type === 'days_of_week' ? (
                    <>
                      <Col xs={12}>
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Bake Days</Form.Label>
                        <div className="d-flex gap-2 flex-wrap mt-1">
                          {WEEKDAYS.map((d, i) => (
                            <button key={i} onClick={() => toggleDay('bake_days', i)}
                              style={{ minWidth: 52, height: 40, borderRadius: '20px', border: `2px solid ${form.bake_days.includes(i) ? '#059669' : '#e2e8f0'}`, background: form.bake_days.includes(i) ? '#059669' : 'white', color: form.bake_days.includes(i) ? 'white' : '#374151', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 10px' }}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Last Order Days <span style={{ color: '#9ca3af', fontWeight: 400 }}>(when orders close)</span></Form.Label>
                        <div className="d-flex gap-2 flex-wrap mt-1">
                          {WEEKDAYS.map((d, i) => (
                            <button key={i} onClick={() => toggleDay('last_order_days', i)}
                              style={{ minWidth: 52, height: 40, borderRadius: '20px', border: `2px solid ${form.last_order_days.includes(i) ? '#f59e0b' : '#e2e8f0'}`, background: form.last_order_days.includes(i) ? '#fef3c7' : 'white', color: form.last_order_days.includes(i) ? '#92400e' : '#374151', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 10px' }}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </Col>
                      <Col xs={6}>
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Cut-off Time</Form.Label>
                        <Form.Control type="time" size="sm" value={form.last_order_time} onChange={e => setForm((p: any) => ({ ...p, last_order_time: e.target.value }))} />
                      </Col>
                      <Col xs={6}>
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Lead Days</Form.Label>
                        <Form.Control type="number" size="sm" min={0} max={14} value={form.order_lead_days} onChange={e => setForm((p: any) => ({ ...p, order_lead_days: parseInt(e.target.value) || 1 }))} />
                      </Col>
                    </>
                  ) : (
                    <Col xs={12}>
                      <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Upcoming Bake Dates</Form.Label>
                      <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 10 }}>Customers will choose from these dates at checkout. Dates whose cutoff has passed are hidden automatically.</p>
                      {(form.bake_exact_dates || []).map((entry: any, idx: number) => (
                        <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, background: '#f8fafc', borderRadius: '10px', padding: '10px 12px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 2 }}>Bake Date</div>
                            <Form.Control type="date" size="sm" value={entry.date || ''} onChange={e => {
                              const updated = [...form.bake_exact_dates];
                              updated[idx] = { ...updated[idx], date: e.target.value };
                              setForm((p: any) => ({ ...p, bake_exact_dates: updated }));
                            }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 2 }}>Cutoff Date</div>
                            <Form.Control type="date" size="sm" value={entry.cutoff_date || ''} onChange={e => {
                              const updated = [...form.bake_exact_dates];
                              updated[idx] = { ...updated[idx], cutoff_date: e.target.value };
                              setForm((p: any) => ({ ...p, bake_exact_dates: updated }));
                            }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 2 }}>Cutoff Time</div>
                            <Form.Control type="time" size="sm" value={entry.cutoff_time || ''} onChange={e => {
                              const updated = [...form.bake_exact_dates];
                              updated[idx] = { ...updated[idx], cutoff_time: e.target.value };
                              setForm((p: any) => ({ ...p, bake_exact_dates: updated }));
                            }} />
                          </div>
                          <button onClick={() => setForm((p: any) => ({ ...p, bake_exact_dates: form.bake_exact_dates.filter((_: any, i: number) => i !== idx) }))}
                            style={{ background: '#fef2f2', border: 'none', borderRadius: '8px', width: 32, height: 32, cursor: 'pointer', color: '#ef4444', fontWeight: 700, fontSize: '1rem', flexShrink: 0, marginTop: 14 }}>
                            ×
                          </button>
                        </div>
                      ))}
                      <button onClick={() => setForm((p: any) => ({ ...p, bake_exact_dates: [...(p.bake_exact_dates || []), { date: '', cutoff_date: '', cutoff_time: '12:00' }] }))}
                        style={{ background: '#f0fdf4', border: '1.5px dashed #86efac', borderRadius: '10px', padding: '8px 18px', color: '#059669', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', width: '100%' }}>
                        + Add Bake Date
                      </button>
                    </Col>
                  )}
                  {/* Login Style */}
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Login Style</Form.Label>
                    <div className="d-flex gap-2 mt-1">
                      {[{ val: 'modal', label: 'Popup Modal' }, { val: 'page', label: 'Full Page' }].map(opt => (
                        <button key={opt.val} onClick={() => setForm((p: any) => ({ ...p, login_style: opt.val }))}
                          style={{ padding: '8px 20px', borderRadius: '20px', border: `2px solid ${form.login_style === opt.val ? '#6366f1' : '#e2e8f0'}`, background: form.login_style === opt.val ? '#6366f1' : 'white', color: form.login_style === opt.val ? 'white' : '#374151', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6, marginBottom: 0 }}>
                      {form.login_style === 'page' ? 'Customers are taken to a dedicated sign-in screen.' : 'Login appears as a centered popup overlay.'}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* ── Fulfillment ─────────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>FULFILLMENT</div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: 8 }}>Fulfillment Mode</div>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 10px' }}>Choose whether your store offers delivery, pickup, or both.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[{ value: 'both', label: 'Delivery & Pickup', desc: 'Customers enter an address for delivery.' },
                      { value: 'delivery', label: 'Delivery Only', desc: 'All orders require a delivery address.' },
                      { value: 'pickup', label: 'Pickup Only', desc: 'No address needed — customers pick up their order.' }]
                      .map(opt => (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${form.fulfillment_mode === opt.value ? '#059669' : '#e5e7eb'}`, background: form.fulfillment_mode === opt.value ? '#f0fdf4' : '#fff' }}>
                          <input type="radio" name="fulfillment_mode" value={opt.value} checked={form.fulfillment_mode === opt.value} onChange={() => setForm((p: any) => ({ ...p, fulfillment_mode: opt.value }))} style={{ marginTop: 2 }} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1f2937' }}>{opt.label}</div>
                            <div style={{ fontSize: '0.73rem', color: '#6b7280' }}>{opt.desc}</div>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>
                {form.fulfillment_mode !== 'pickup' && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Delivery Fee</div>
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 8px' }}>Flat delivery fee added per order. Set to $0 for free delivery.</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 180 }}>
                      <span style={{ color: '#374151', fontWeight: 600 }}>$</span>
                      <Form.Control type="number" size="sm" min={0} step={0.5} value={form.delivery_fee}
                        onChange={e => setForm((p: any) => ({ ...p, delivery_fee: parseFloat(e.target.value) || 0 }))} />
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* ── Fees & Pricing ───────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>FEES &amp; PRICING</div>

                {/* Tax */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: 8 }}>Sales Tax</div>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 8px' }}>Applied to the product subtotal only — not to service fees or tip.</p>
                  <Row className="g-2">
                    <Col xs={4}>
                      <Form.Label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Rate (%)</Form.Label>
                      <Form.Control type="number" size="sm" min={0} max={30} step={0.1} value={form.tax_rate}
                        onChange={e => setForm((p: any) => ({ ...p, tax_rate: parseFloat(e.target.value) || 0 }))} />
                    </Col>
                    <Col xs={8}>
                      <Form.Label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Label shown to customer</Form.Label>
                      <Form.Control type="text" size="sm" placeholder="Tax" value={form.tax_label}
                        onChange={e => setForm((p: any) => ({ ...p, tax_label: e.target.value }))} />
                    </Col>
                  </Row>
                </div>

                {/* Platform fee */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Sourdoughsy Platform Fee</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: '0.78rem', color: '#6366f1', background: '#eef2ff', borderRadius: '6px', padding: '2px 8px', fontWeight: 700 }}>
                      Your rate: {form.platform_fee_pct}%
                    </span>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>based on your subscription plan</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 8px' }}>
                    Choose how much of this fee to pass on to customers. 0% = you absorb it, 100% = customer pays it.
                  </p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="range" min={0} max={100} step={5} value={form.platform_fee_customer_pct}
                      onChange={e => setForm((p: any) => ({ ...p, platform_fee_customer_pct: parseInt(e.target.value) }))}
                      style={{ flex: 1 }} />
                    <span style={{ fontWeight: 700, minWidth: 40, textAlign: 'right', color: '#374151' }}>{form.platform_fee_customer_pct}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                    <span>You absorb all</span>
                    <span>50/50 split</span>
                    <span>Customer pays all</span>
                  </div>
                </div>

                {/* CC fee */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Credit Card Processing Fee</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: '0.78rem', color: '#6b7280', background: '#f8fafc', borderRadius: '6px', padding: '2px 8px', fontWeight: 600 }}>
                      Stripe: 2.9% + $0.30 per transaction
                    </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 8px' }}>
                    Only visible to customers when Stripe payments are enabled. 0% = you absorb it.
                  </p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="range" min={0} max={100} step={5} value={form.cc_fee_customer_pct}
                      onChange={e => setForm((p: any) => ({ ...p, cc_fee_customer_pct: parseInt(e.target.value) }))}
                      style={{ flex: 1 }} />
                    <span style={{ fontWeight: 700, minWidth: 40, textAlign: 'right', color: '#374151' }}>{form.cc_fee_customer_pct}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                    <span>You absorb all</span>
                    <span>50/50 split</span>
                    <span>Customer pays all</span>
                  </div>
                </div>

                {/* Tip */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151' }}>Customer Tips</div>
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Customers can add a tip at checkout based on the product subtotal.</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.8rem', color: form.tip_enabled ? '#059669' : '#94a3b8' }}>{form.tip_enabled ? 'On' : 'Off'}</span>
                      <div onClick={() => setForm((p: any) => ({ ...p, tip_enabled: !p.tip_enabled }))}
                        style={{ width: 40, height: 22, borderRadius: '11px', background: form.tip_enabled ? '#059669' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.tip_enabled ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                  </div>
                  {form.tip_enabled && (
                    <div>
                      <Form.Label style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Preset percentage options</Form.Label>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {(form.tip_percentages || []).map((pct: number, idx: number) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '4px 8px', gap: 4 }}>
                            <input type="number" min={1} max={100} value={pct}
                              onChange={e => {
                                const arr = [...form.tip_percentages];
                                arr[idx] = parseInt(e.target.value) || 0;
                                setForm((p: any) => ({ ...p, tip_percentages: arr }));
                              }}
                              style={{ width: 40, border: 'none', background: 'none', fontWeight: 700, fontSize: '0.85rem', color: '#059669', padding: 0, textAlign: 'center', outline: 'none' }} />
                            <span style={{ color: '#059669', fontSize: '0.85rem', fontWeight: 700 }}>%</span>
                            <button onClick={() => setForm((p: any) => ({ ...p, tip_percentages: form.tip_percentages.filter((_: number, i: number) => i !== idx) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', fontSize: '0.85rem', lineHeight: 1 }}>×</button>
                          </div>
                        ))}
                        <button onClick={() => setForm((p: any) => ({ ...p, tip_percentages: [...(p.tip_percentages || []), 10] }))}
                          style={{ background: '#f8fafc', border: '1.5px dashed #e2e8f0', borderRadius: '8px', padding: '4px 12px', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer' }}>
                          + Add
                        </button>
                      </div>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>Customers can also type a custom amount.</p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* ── Custom Domain ────────────────────────────── */}
            {customDomainEnabled ? (
              <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px', borderLeft: '3px solid #6366f1' }}>
                <Card.Body className="p-4">
                  <div style={sectionLabel}>CUSTOM DOMAIN</div>
                  <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Your domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="shop.yourbakery.com"
                    value={form.custom_domain || ''}
                    onChange={e => {
                      // Strip protocol and trailing slashes so user can paste full URLs
                      const raw = e.target.value.trim().toLowerCase()
                        .replace(/^https?:\/\//, '')
                        .replace(/\/.*$/, '');
                      setForm((p: any) => ({ ...p, custom_domain: raw }));
                    }}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <p style={{ fontSize: '0.78rem', color: '#6366f1', marginTop: 8, marginBottom: 4 }}>
                    Point your domain's DNS <strong>A record</strong> to <code style={{ background: '#eef2ff', padding: '1px 5px', borderRadius: 4 }}>45.79.211.144</code> and set <strong>CNAME</strong> or proxy via Cloudflare. SSL is handled automatically.
                  </p>
                  {form.custom_domain && (
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4 }}>
                      Your store will be live at{' '}
                      <a href={`https://${form.custom_domain}/store`} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1' }}>
                        https://{form.custom_domain}/store
                      </a>
                    </p>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Card className="border-0 mb-3" style={{ borderRadius: '14px', background: '#f8fafc', border: '1px dashed #e2e8f0' }}>
                <Card.Body className="p-4">
                  <div style={{ ...sectionLabel, color: '#cbd5e1' }}>CUSTOM DOMAIN</div>
                  <p style={{ fontSize: '0.83rem', color: '#94a3b8', margin: 0 }}>
                    Custom domain support is not enabled for your account. Contact support to upgrade.
                  </p>
                </Card.Body>
              </Card>
            )}

            {/* ── Stripe Payments ──────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px', borderLeft: '3px solid #7c3aed' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>STRIPE PAYMENTS</div>
                {connectError && <Alert variant="danger" className="mb-3" style={{ fontSize: '0.82rem' }}>{connectError}</Alert>}

                {connectStatus?.connected ? (
                  <>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: connectStatus.charges_enabled ? '#10b981' : '#f59e0b', display: 'inline-block' }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {connectStatus.charges_enabled ? 'Stripe Connected — Payments Active' : 'Connected — Awaiting Stripe Approval'}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 12 }}>
                      Account: <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{connectStatus.account_id}</code>
                    </p>
                    {platformFeePct && (
                      <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                        <span style={{ fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>Platform fee: {platformFeePct}% per transaction</span>
                        <p style={{ fontSize: '0.76rem', color: '#9ca3af', margin: '4px 0 0' }}>
                          + Stripe fees (2.9% + $0.30) deducted directly from your Stripe balance by Stripe.
                        </p>
                      </div>
                    )}
                    <Button
                      size="sm" variant="outline-danger"
                      style={{ borderRadius: 8, fontSize: '0.8rem' }}
                      disabled={connectLoading}
                      onClick={async () => {
                        if (!window.confirm('Disconnect Stripe? Retail and wholesale payments will stop working.')) return;
                        setConnectLoading(true); setConnectError('');
                        try {
                          await paymentsAPI.disconnectStripe();
                          setConnectStatus({ connected: false });
                        } catch (e: any) {
                          setConnectError(e?.response?.data?.error || 'Failed to disconnect');
                        } finally { setConnectLoading(false); }
                      }}
                    >
                      {connectLoading ? 'Disconnecting…' : 'Disconnect Stripe'}
                    </Button>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: '0.83rem', color: '#64748b', marginBottom: 14 }}>
                      Connect your Stripe account to accept <strong>retail webstore payments</strong> and send <strong>wholesale payment links</strong> to your B2B clients.
                      The platform fee for your plan is <strong>{platformFeePct ? platformFeePct + '%' : '5%'}</strong> per transaction.
                    </p>
                    <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                      <p style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600, margin: 0 }}>How it works</p>
                      <ol style={{ fontSize: '0.78rem', color: '#64748b', margin: '6px 0 0', paddingLeft: 18 }}>
                        <li>Connect your Stripe account (takes ~2 min)</li>
                        <li>Enable payments on your webstore — customers check out with card</li>
                        <li>Generate payment links for wholesale orders — share with clients</li>
                        <li>Money goes directly to your Stripe; platform fee is deducted automatically</li>
                      </ol>
                    </div>
                    <Button
                      style={{ background: '#7c3aed', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: '0.9rem', padding: '9px 22px' }}
                      disabled={connectLoading}
                      onClick={async () => {
                        setConnectLoading(true); setConnectError('');
                        try {
                          const res = await paymentsAPI.getConnectOnboardingUrl();
                          window.location.href = res.data.url;
                        } catch (e: any) {
                          setConnectError(e?.response?.data?.error || 'Failed to start Stripe onboarding');
                          setConnectLoading(false);
                        }
                      }}
                    >
                      {connectLoading ? 'Redirecting to Stripe…' : '⚡ Connect Stripe Account'}
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>

            <Button variant="success" className="w-100 py-3" style={{ borderRadius: '12px', fontWeight: 700, fontSize: '1rem', background: '#059669', border: 'none' }} onClick={handleSave} disabled={saving}>
              <Cog8ToothIcon style={{ width: 18, height: 18, marginRight: 8 }} />
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Col>
        </Row>

        {/* ── Retail Products Panel ────────────────────────── */}
        <Card className="border-0 shadow-sm mt-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Retail Products</div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Toggle which products appear on your public store</div>
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-success"
                  style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                  onClick={async () => {
                    const allIds = allProducts.map((p: any) => p.id);
                    await companyAPI.setRetailPublished(allIds, true);
                    setAllProducts(prev => prev.map(p => ({ ...p, retail_published: true })));
                  }}
                >
                  Publish All
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                  onClick={async () => {
                    const allIds = allProducts.map((p: any) => p.id);
                    await companyAPI.setRetailPublished(allIds, false);
                    setAllProducts(prev => prev.map(p => ({ ...p, retail_published: false })));
                  }}
                >
                  Unpublish All
                </Button>
              </div>
            </div>
            {allProducts.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', textAlign: 'center', padding: '20px 0' }}>
                No products found. Add products in the Products section first.
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                {allProducts.map((product: any) => (
                  <div
                    key={product.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: '10px',
                      background: product.retail_published ? '#f0fdf4' : '#f8fafc',
                      border: `1px solid ${product.retail_published ? '#bbf7d0' : '#e2e8f0'}`,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        {product.category?.name || 'Uncategorized'} · ${parseFloat(product.price_per_unit || 0).toFixed(2)}/{product.unit}
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id={`retail-toggle-${product.id}`}
                      checked={!!product.retail_published}
                      disabled={togglingId === product.id}
                      onChange={() => toggleProductRetail(product)}
                      style={{ marginLeft: 12 }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ── Store Pages Panel ─────────────────────────── */}
        <Card className="border-0 shadow-sm mt-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Store Pages</div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Custom pages like About, Our Bread, FAQ — shown in your store nav</div>
              </div>
              <Button
                size="sm"
                variant="success"
                style={{ borderRadius: '8px', background: '#059669', border: 'none', fontWeight: 600 }}
                onClick={() => {
                  setEditingPage(null);
                  setPageForm({ title: '', slug: '', content: '', is_published: true, sort_order: pages.length });
                  setPageFormError('');
                  setPageModal(true);
                }}
              >
                <PlusIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                New Page
              </Button>
            </div>
            {pages.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', textAlign: 'center', padding: '20px 0' }}>
                No pages yet. Create an "About" or "Our Bread" page to add navigation links to your store.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pages.map(pg => (
                  <div key={pg.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: pg.is_published ? '#f0fdf4' : '#f8fafc', border: `1px solid ${pg.is_published ? '#bbf7d0' : '#e2e8f0'}` }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>{pg.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>/{pg.slug} · {pg.is_published ? <span style={{ color: '#059669' }}>Published</span> : <span style={{ color: '#ef4444' }}>Draft</span>}</div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-secondary" style={{ borderRadius: '6px', padding: '3px 8px' }}
                        onClick={() => {
                          setEditingPage(pg);
                          setPageForm({ title: pg.title, slug: pg.slug, content: pg.content, is_published: pg.is_published, sort_order: pg.sort_order });
                          setPageFormError('');
                          setPageModal(true);
                        }}>
                        <PencilIcon style={{ width: 13, height: 13 }} />
                      </Button>
                      <Button size="sm" variant="outline-danger" style={{ borderRadius: '6px', padding: '3px 8px' }}
                        onClick={async () => {
                          if (!window.confirm(`Delete "${pg.title}"?`)) return;
                          await companyAPI.deleteStorePage(pg.id);
                          setPages(prev => prev.filter(p => p.id !== pg.id));
                        }}>
                        <TrashIcon style={{ width: 13, height: 13 }} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ── Contact Messages Inbox ────────────────────── */}
        <Card className="border-0 shadow-sm mt-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  Contact Messages
                  {contactMessages.filter(m => !m.is_read).length > 0 && (
                    <span style={{ marginLeft: 8, background: '#ef4444', color: 'white', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px' }}>
                      {contactMessages.filter(m => !m.is_read).length} new
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Messages submitted through your store's contact form</div>
              </div>
            </div>
            {contactMessages.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', textAlign: 'center', padding: '20px 0' }}>No messages yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {contactMessages.map(msg => (
                  <div key={msg.id}>
                    <div
                      onClick={() => setContactMsg(contactMsg?.id === msg.id ? null : msg)}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 4px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: 'none' }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: msg.is_read ? 'transparent' : '#3b82f6', marginTop: 6, flexShrink: 0, border: msg.is_read ? '1px solid #cbd5e1' : 'none' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span style={{ fontWeight: msg.is_read ? 500 : 700, fontSize: '0.88rem', color: '#1e293b' }}>{msg.name}</span>
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>{new Date(msg.created_at).toLocaleDateString()}</span>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{msg.email}{msg.subject ? ` · ${msg.subject}` : ''}</div>
                        {contactMsg?.id !== msg.id && (
                          <div style={{ fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{msg.message}</div>
                        )}
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0">
                        <button title={msg.is_read ? 'Mark unread' : 'Mark read'} onClick={async e => { e.stopPropagation(); await companyAPI.markContactRead(msg.id, !msg.is_read); setContactMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: !m.is_read } : m)); }}
                          style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 7px', cursor: 'pointer', fontSize: '0.72rem', color: '#64748b' }}>
                          {msg.is_read ? '● Unread' : '✓ Read'}
                        </button>
                        <button title="Delete" onClick={async e => { e.stopPropagation(); if (!window.confirm('Delete this message?')) return; await companyAPI.deleteContactMessage(msg.id); setContactMessages(prev => prev.filter(m => m.id !== msg.id)); if (contactMsg?.id === msg.id) setContactMsg(null); }}
                          style={{ background: 'none', border: '1px solid #fee2e2', borderRadius: '6px', padding: '3px 7px', cursor: 'pointer', color: '#ef4444', fontSize: '0.72rem' }}>
                          ✕
                        </button>
                      </div>
                    </div>
                    {contactMsg?.id === msg.id && (
                      <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px 16px', margin: '4px 0 8px 20px', fontSize: '0.85rem', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {msg.phone && <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 8 }}>📞 {msg.phone}</div>}
                        {msg.message}
                        <div style={{ marginTop: 10 }}>
                          <a href={`mailto:${msg.email}`} style={{ fontSize: '0.78rem', color: '#059669', fontWeight: 600, textDecoration: 'none' }}>Reply via email →</a>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Crop Modal */}
      {cropSrc && cropTarget && (
        <CropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          aspect={cropTarget === 'logo' ? undefined : 16 / 9}
          cropShape="rect"
          format={cropTarget === 'logo' ? 'png' : 'jpeg'}
          title={cropTarget === 'logo' ? 'Crop Logo' : 'Crop Hero Image'}
          onDone={handleCropDone}
          onClose={() => { setCropSrc(null); setCropTarget(null); }}
        />
      )}

      {/* ── Pages Modal ──────────────────────────────────────── */}
      <Modal show={pageModal} onHide={() => setPageModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
            {editingPage ? 'Edit Page' : 'New Page'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {pageFormError && <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>{pageFormError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Page Title</Form.Label>
            <Form.Control size="sm" value={pageForm.title} onChange={e => {
              const title = e.target.value;
              const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              setPageForm(p => ({ ...p, title, slug: editingPage ? p.slug : slug }));
            }} placeholder="e.g. About Us" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>URL Slug</Form.Label>
            <Form.Control size="sm" value={pageForm.slug} onChange={e => setPageForm(p => ({ ...p, slug: e.target.value }))} placeholder="e.g. about-us" />
            <Form.Text muted>Accessible at /store/pages/{pageForm.slug || '...'}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={pageForm.content}
              onChange={e => setPageForm(p => ({ ...p, content: e.target.value }))}
              placeholder="Write your page content here. You can use HTML tags for formatting."
              style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
            />
          </Form.Group>
          <div className="d-flex gap-3 align-items-center">
            <Form.Check
              type="switch"
              id="page-published"
              checked={pageForm.is_published}
              onChange={e => setPageForm(p => ({ ...p, is_published: e.target.checked }))}
              label={<span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Published</span>}
            />
            <Form.Group className="d-flex align-items-center gap-2 ms-auto">
              <Form.Label style={{ fontSize: '0.82rem', marginBottom: 0, color: '#64748b' }}>Order</Form.Label>
              <Form.Control type="number" size="sm" value={pageForm.sort_order} onChange={e => setPageForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={{ width: 64 }} />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9' }}>
          <Button variant="light" style={{ borderRadius: '8px' }} onClick={() => setPageModal(false)}>Cancel</Button>
          <Button
            variant="success"
            style={{ borderRadius: '8px', background: '#059669', border: 'none' }}
            onClick={async () => {
              setPageFormError('');
              if (!pageForm.title.trim()) { setPageFormError('Title is required.'); return; }
              if (!pageForm.slug.trim())  { setPageFormError('Slug is required.'); return; }
              try {
                if (editingPage) {
                  const res = await companyAPI.updateStorePage(editingPage.id, pageForm);
                  setPages(prev => prev.map(pg => pg.id === editingPage.id ? res.data : pg));
                } else {
                  const res = await companyAPI.createStorePage(pageForm);
                  setPages(prev => [...prev, res.data]);
                }
                setPageModal(false);
              } catch (err: any) {
                const d = err?.response?.data;
                setPageFormError(d?.slug?.[0] || d?.title?.[0] || 'Failed to save page.');
              }
            }}
          >
            {editingPage ? 'Save Changes' : 'Create Page'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
}
